import type { IPopupTabHandlerModule } from '../types';

/**
 * Modul für Popup-Tab-Handler-Funktionalität
 * Verwaltet den Fokus bei Erscheinen von Popup-Elementen
 */
const PopupTabHandlerModule: IPopupTabHandlerModule = {
    previousFocusedElement: null,
    focusStack: [] as HTMLElement[],
   
    /**
     * Initialisiert den Popup-Observer mit MutationObserver
     * Beobachtet das DOM auf Elemente mit data-popup="true"
     */
    initializePopupObserver(): void {
        // Wenn bereits ein Observer existiert, stoppe ihn zuerst
        this.stopPopupObserver();
        
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                // Prüfe auf hinzugefügte Nodes
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const element = node as HTMLElement;
                        
                        // Prüfe, ob das Element selbst ein Popup ist
                        if (element.hasAttribute('data-popup')) {
                            this.handlePopupAppearance(element);
                        }
                        
                        // Prüfe auch auf verschachtelte Popups
                        const popups = element.querySelectorAll<HTMLElement>('[data-popup]');
                        popups.forEach(popup => this.handlePopupAppearance(popup));
                    }
                });

                // Prüfe auf entfernte Nodes
                mutation.removedNodes.forEach((node) => {                    
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const element = node as HTMLElement;

                        // Prüfe, ob das Element selbst ein Popup ist
                        if (element.hasAttribute('data-popup')) {                                                    
                            this.restorePreviousFocus();

                            return;
                        }
                        
                        // Prüfe auch auf verschachtelte Popups
                        const popups = element.querySelectorAll<HTMLElement>('[data-popup]');
                        if (popups.length > 0) {
                            this.restorePreviousFocus();

                            return;
                        }
                    }
                });
            });
        });

        // Beobachte das gesamte Dokument
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // Speichere Observer-Instanz für spätere Verwendung
        (this as any).observerInstance = observer;
    },

    /**
     * Stoppt den Popup-Observer
     */
    stopPopupObserver(): void {
        if ((this as any).observerInstance) {
            (this as any).observerInstance.disconnect();
            (this as any).observerInstance = null;
        }
    },

    /**
     * Behandelt das Erscheinen eines Popups
     * @param popupElement - Das erschienene Popup-Element
     */
    handlePopupAppearance(popupElement: HTMLElement): void {
        // Speichere das aktuell fokussierte Element auf den Stack
        if (document.activeElement && document.activeElement !== document.body) {
            const activeEl = document.activeElement as HTMLElement;
            
            // Nur hinzufügen, wenn es nicht bereits ein Popup ist oder das letzte Element im Stack
            if (!activeEl.hasAttribute('data-popup') || this.focusStack[this.focusStack.length - 1] !== activeEl) {
                this.focusStack.push(activeEl);
                
                // Stelle sicher, dass das Element eine ID hat für spätere Wiederherstellung
                if (!activeEl.id) {
                    activeEl.id = `etask-focus-restore-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
                }
            }
        }

        popupElement.focus();
        popupElement.tabIndex = -1; // Sicherstellen, dass das Popup fokussierbar ist
    },

    /**
     * Stellt den vorherigen Fokus wieder her
     * @returns true, wenn erfolgreich wiederhergestellt wurde, sonst false
     */
    restorePreviousFocus(): boolean {
        // Pop das letzte Element vom Stack
        const previousElement = this.focusStack.pop();
        
        if (previousElement) {
            // Prüfe ob das Element noch im DOM existiert
            if (document.contains(previousElement)) {
                previousElement.focus();
                return true;
            } else {
                // Wenn Element nicht mehr existiert, versuche das nächste vom Stack
                return this.restorePreviousFocus();
            }
        }
        
        // Fallback: Verwende das alte previousFocusedElement wenn Stack leer ist
        if (this.previousFocusedElement) {
            this.previousFocusedElement.focus();
            this.previousFocusedElement = null;
            return true;
        }
        
        return false;
    }
};

export default PopupTabHandlerModule;
